/**
 * Dedupe helper for the continuous badge scanner.
 *
 * A camera pointed at one badge decodes the same QR many times per second, so we
 * must ignore repeat reads of the same code within a short window — otherwise a
 * single badge held up would fire a burst of check-in requests. This tracks the
 * last accepted code + time and answers "should we act on this decode now?".
 *
 * Pure/stateful-but-testable: no React, no camera. The scanner instantiates one
 * per session.
 */
export const DEFAULT_DEDUPE_MS = 3000

export class ScanDeduper {
  private lastCode: string | null = null
  private lastAt = 0

  constructor(private readonly windowMs: number = DEFAULT_DEDUPE_MS) {}

  /**
   * Returns true if this decoded value should be acted on now (i.e. it's a new
   * code, or the same code but the dedupe window has elapsed). Accepting a value
   * records it so subsequent identical reads within the window are rejected.
   *
   * @param code the decoded QR string
   * @param now  current epoch ms (injectable for testing; defaults to Date.now)
   */
  accept(code: string, now: number = Date.now()): boolean {
    const trimmed = (code || '').trim()
    if (!trimmed) return false
    if (trimmed === this.lastCode && now - this.lastAt < this.windowMs) {
      return false
    }
    this.lastCode = trimmed
    this.lastAt = now
    return true
  }

  /** Clear state so the next read of any code is accepted immediately. */
  reset(): void {
    this.lastCode = null
    this.lastAt = 0
  }
}

/** Basic shape check for our badge token (`VCK1.<id>.<sig>`), to ignore
 *  unrelated QR codes the camera might catch. Lenient: real validation is the
 *  server's HMAC check. */
export function looksLikeBadgeToken(value: string): boolean {
  const parts = (value || '').trim().split('.')
  return parts.length === 3 && parts[0] === 'VCK1' && parts[1].length > 0 && parts[2].length > 0
}
